import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { studentComplaintAPI } from '../services/api';
import './Auth.css';

const StudentComplaint = () => {
  const [formData, setFormData] = useState({
    student_name: '',
    student_phone: '',
    complaint_text: '',
    phone_model: '',
    os_version: ''
  });
  const [screenshots, setScreenshots] = useState([]);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const navigate = useNavigate();

  // Load reCAPTCHA script
  useEffect(() => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || 'YOUR_SITE_KEY';

    if (document.querySelector(`script[src*="recaptcha"]`)) {
      setRecaptchaReady(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const checkReady = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.ready) {
          window.grecaptcha.ready(() => {
            setRecaptchaReady(true);
            clearInterval(checkReady);
          });
        }
      }, 100);
    };
    document.head.appendChild(script);
  }, []);

  // Note: Complaint type is automatically set to "Student Complaints" by backend
  // No need to load or display complaint types for students

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    // Validate file count (max 5)
    if (files.length > 5) {
      setError('Maximum 5 screenshots allowed');
      return;
    }

    // Validate file size (max 5MB per file)
    const maxSize = 5 * 1024 * 1024; // 5MB
    for (let file of files) {
      if (file.size > maxSize) {
        setError(`File ${file.name} is too large. Maximum 5MB per file.`);
        return;
      }
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    for (let file of files) {
      if (!allowedTypes.includes(file.type)) {
        setError(`File ${file.name} is not a valid image. Only JPEG, PNG, GIF, and WebP are allowed.`);
        return;
      }
    }

    setScreenshots(files);
    setError('');
  };

  const getRecaptchaToken = async () => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || 'YOUR_SITE_KEY';

    if (!window.grecaptcha || !recaptchaReady) {
      console.warn('reCAPTCHA not ready, proceeding without token');
      return null;
    }

    try {
      const token = await window.grecaptcha.execute(siteKey, { action: 'submit_complaint' });
      return token;
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate phone number
    if (!/^[0-9]{10}$/.test(formData.student_phone)) {
      setError('Phone number must be exactly 10 digits');
      setLoading(false);
      return;
    }

    try {
      const recaptchaToken = await getRecaptchaToken();

      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('student_name', formData.student_name);
      submitData.append('student_phone', formData.student_phone);
      submitData.append('complaint_text', formData.complaint_text);
      submitData.append('phone_model', formData.phone_model);
      submitData.append('os_version', formData.os_version);
      submitData.append('recaptchaToken', recaptchaToken);

      // Add screenshots
      screenshots.forEach((file) => {
        submitData.append('screenshots', file);
      });

      const response = await studentComplaintAPI.submitComplaint(submitData);

      setSuccess(response.data);
    } catch (err) {
      const errorData = err.response?.data;
      const errorMessage = errorData?.message || errorData?.error || 'Failed to submit complaint. Please try again.';
      setError(errorMessage);

      if (errorMessage.includes('reCAPTCHA') || errorMessage.includes('bot')) {
        setError('Security verification failed. Please refresh the page and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-backdrop"></div>
        <div className="auth-card fade-in" style={{ maxWidth: '600px' }}>
          <div className="auth-header">
            <h1 className="auth-title" style={{ color: '#10b981' }}>
              ✓ Complaint Submitted Successfully!
            </h1>
            <p className="auth-subtitle">Your complaint has been recorded</p>
          </div>

          <div className="info-message" style={{ marginBottom: '24px', background: 'rgba(16, 185, 129, 0.1)', borderColor: 'rgba(16, 185, 129, 0.3)' }}>
            <h3 style={{ marginTop: 0 }}>Important: Save These Details</h3>
            <div style={{ fontSize: '18px', margin: '16px 0' }}>
              <p><strong>Ticket ID:</strong> <span style={{ color: 'var(--primary)', fontSize: '20px', fontWeight: 'bold' }}>{success.ticket_id}</span></p>
              <p><strong>Phone Number:</strong> <span style={{ fontSize: '18px' }}>{success.phone_number}</span></p>
            </div>
            <p style={{ fontSize: '14px', marginBottom: 0 }}>
              You will need both of these to track your complaint status.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
            <button
              onClick={() => navigate('/track-complaint')}
              className="btn btn-primary btn-block"
            >
              Track My Complaint
            </button>
            <button
              onClick={() => {
                setSuccess(null);
                setFormData({
                  student_name: '',
                  student_phone: '',
                  complaint_text: '',
                  phone_model: '',
                  os_version: ''
                });
                setScreenshots([]);
              }}
              className="btn btn-secondary btn-block"
            >
              Submit Another Complaint
            </button>
            <button
              onClick={() => navigate('/login')}
              className="auth-link"
              style={{ marginTop: '8px' }}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-backdrop"></div>
      <div className="auth-card fade-in" style={{ maxWidth: '600px' }}>
        <div className="auth-header">
          <h1 className="auth-title">Submit Student Complaint</h1>
          <p className="auth-subtitle">No login required - Track with phone number</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input
              type="text"
              name="student_name"
              className="form-input"
              value={formData.student_name}
              onChange={handleChange}
              required
              autoFocus
              placeholder="Enter your full name"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Phone Number * (10 digits)</label>
            <input
              type="tel"
              name="student_phone"
              className="form-input"
              value={formData.student_phone}
              onChange={handleChange}
              required
              pattern="[0-9]{10}"
              maxLength="10"
              placeholder="9876543210"
            />
            <small style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
              You'll need this phone number to track your complaint
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Phone Model * (e.g., iPhone 13, Samsung Galaxy S21)</label>
            <input
              type="text"
              name="phone_model"
              className="form-input"
              value={formData.phone_model}
              onChange={handleChange}
              required
              placeholder="iPhone 13"
            />
          </div>

          <div className="form-group">
            <label className="form-label">OS Version * (e.g., iOS 16.5, Android 12)</label>
            <input
              type="text"
              name="os_version"
              className="form-input"
              value={formData.os_version}
              onChange={handleChange}
              required
              placeholder="iOS 16.5"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Complaint Details *</label>
            <textarea
              name="complaint_text"
              className="form-input"
              value={formData.complaint_text}
              onChange={handleChange}
              required
              rows="6"
              placeholder="Please describe your complaint in detail..."
              style={{ resize: 'vertical', minHeight: '120px' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Screenshots (Optional - Max 5 files, 5MB each)</label>
            <input
              type="file"
              name="screenshots"
              className="form-input"
              onChange={handleFileChange}
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              multiple
              style={{ padding: '8px' }}
            />
            <small style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
              Accepted formats: JPEG, PNG, GIF, WebP
            </small>
            {screenshots.length > 0 && (
              <div style={{ marginTop: '8px', color: 'var(--text-secondary)', fontSize: '14px' }}>
                {screenshots.length} file(s) selected: {screenshots.map(f => f.name).join(', ')}
              </div>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit Complaint'}
          </button>

          <div className="recaptcha-notice">
            <small>
              This site is protected by reCAPTCHA and the Google{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>{' '}
              and{' '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>{' '}
              apply.
            </small>
          </div>
        </form>

        <div className="auth-footer">
          <p>
            Want to track an existing complaint?{' '}
            <button onClick={() => navigate('/track-complaint')} className="auth-link">
              Track Complaint
            </button>
          </p>
          <p>
            Already have an account?{' '}
            <button onClick={() => navigate('/login')} className="auth-link">
              Login
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default StudentComplaint;
